import { ApolloClient, gql } from 'apollo-boost'
import {
  Box,
  Button,
  ButtonBase,
  Card,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import { FlexHorizontal, Spacer } from '@vestaboard/installables'
import React, { useCallback, useState } from 'react'
import {
  ReservePairingCodes as ReservePairingCodesMutation,
  ReservePairingCodes_reservePairingCode_pairingCode
} from './__generated__/ReservePairingCodes'

import { Alert } from '@material-ui/lab'
import { ApolloConsumer } from '@apollo/react-hooks'
import HelpIcon from '@material-ui/icons/HelpOutline'
import { CsvImportButton } from '../../ui/CsvImportButton'
import { UpdateDeviceMutation, UpdateDeviceMutationVariables } from '../../api/__generated__/UpdateDeviceMutation'
import { saveJSONFile } from '../../utils/file'
import { useListDeviceModels } from './useListDeviceModels'

const MUTATION = gql`
  mutation ReservePairingCodes($size: Int!, $model: String!, $type: String!) {
    reservePairingCode(input: { size: $size }) {
      pairingCode {
        id
        pairingCode
        token(type: $type, modelUuid: $model)
      }
    }
  }
`

const UPDATE_DEVICE_MUTATION = gql`
  mutation BatchUpdateDeviceModel($input: UpdateDeviceInput!) {
    adminUpdateDevice(input: $input) {
      id
      model {
        id
      }
    }
  }
`

const useStyles = makeStyles({
  box: {
    padding: 24
  }
})

interface IReservePairingCodesFn {
  apollo: ApolloClient<object>
}

const legacyDeviceModels = [
  '926b1fc5-dea4-42f0-bec4-cf3c434b1846', // Flagship EVT
  'c8b4ce4e-be94-4880-8287-c74f9804aef9', // Flagship DVT
  '59f500bd-73d5-4433-a4e0-21ec4231e8e6', // Flagship PVT
  '1352a0e0-a5f8-4602-a250-21dc578df961', // Flagship White EVT
  '0df43c47-e600-48e0-a2d9-099506d2fe8c', // Flagship White DVT
  '362bcd53-9945-4c08-a823-f8f518fdd5a2', // Flagship White PVT
  '1d126833-0ba3-4cb7-a767-45f9b16a3c57', // Note Black EVT
  'ca19f6c6-3f44-4906-8e68-2b93b4c7259b' // Note Black DVT
]

export const ReservePairingCodesFn = ({ apollo }: IReservePairingCodesFn) => {
  const [showHelp, setShowHelp] = useState(false)
  const [model, setModel] = useState('')
  const [batchModel, setBatchModel] = useState('')
  const [number, setNumber] = useState(0)
  const [numberRemaining, setNumberRemaining] = useState(0)
  const [isGenerating, setIsGenerating] = useState(false)
  const [generated, setGenerated] = useState('')
  const classes = useStyles()
  const { data: deviceModelsData } = useListDeviceModels()

  const downloadFile = useCallback((data: ReservePairingCodes_reservePairingCode_pairingCode[]) => {
    const outputData = data.map(({ pairingCode, token }) => ({
      pairingCode,
      token
    }))

    saveJSONFile('pairing-codes.json', outputData)
    setIsGenerating(false)
    setGenerated(JSON.stringify(outputData, null, 2))
  }, [])

  const reservePairingCodes = useCallback(
    async (numberRemaining: number, data: ReservePairingCodes_reservePairingCode_pairingCode[]) => {
      setNumberRemaining(numberRemaining)

      // Finished
      if (numberRemaining === 0) {
        return downloadFile(data)
      }

      const modelObjects = deviceModelsData?.deviceModels || []

      const modelObject = modelObjects.find(modelObject => modelObject.id === model)
      const isNote = modelObject?.title.toLowerCase().includes('note')

      try {
        const result = await apollo.mutate<ReservePairingCodesMutation>({
          mutation: MUTATION,
          variables: {
            model,
            size: isNote ? 7 : 6,
            type: isNote ? 'note' : 'flagship'
          }
        })

        // Recurse
        await reservePairingCodes(numberRemaining - 1, [
          ...data,
          result?.data?.reservePairingCode?.pairingCode as ReservePairingCodes_reservePairingCode_pairingCode
        ])
      } catch (e) {
        console.warn('Retrying...')
        await reservePairingCodes(numberRemaining, data)
      }
    },
    [apollo, downloadFile, model, deviceModelsData]
  )

  return (
    <>
      <FlexHorizontal spaceBetween>
        <Typography variant='h4'>Reserve Pairing Codes</Typography>
      </FlexHorizontal>
      <Spacer size='extraLarge' />
      <Card>
        <Box className={classes.box}>
          {isGenerating ? (
            <>
              <LinearProgress variant='determinate' value={((number - numberRemaining) / number) * 100} />
              <Spacer size='large' />
              <Typography>
                Generated {number - numberRemaining} of {number} pairing codes...
              </Typography>
            </>
          ) : (
            <>
              <FlexHorizontal>
                <Typography variant='h5'>Model &nbsp;</Typography>
                <Select
                  value={model}
                  label='Model'
                  onChange={e => {
                    setModel(e.target.value as string)
                  }}>
                  {(deviceModelsData?.deviceModels || [])
                    .filter(model => !legacyDeviceModels.includes(model.id))
                    .map(model => (
                      // @ts-ignore
                      <MenuItem value={model.id}>{model.title}</MenuItem>
                    ))}
                </Select>
              </FlexHorizontal>
              <Spacer size='medium' />
              <FlexHorizontal>
                <Typography variant='h5'>Number of Pairing Codes &nbsp;</Typography>
                <ButtonBase onClick={() => setShowHelp(showHelp => !showHelp)}>
                  <HelpIcon />
                </ButtonBase>
              </FlexHorizontal>
              <Spacer size='medium' />

              <FlexHorizontal>
                <TextField
                  variant='outlined'
                  type='number'
                  value={number}
                  onChange={e => {
                    if (+e.target.value < 0) {
                      setNumber(0)
                      return
                    }
                    setNumber(+e.target.value)
                  }}
                />
                <Box width={7} />
                <Button
                  disabled={!model || number === 0}
                  variant='contained'
                  color='primary'
                  onClick={async () => {
                    setIsGenerating(true)
                    await reservePairingCodes(number, [])
                  }}>
                  Generate
                </Button>
              </FlexHorizontal>
              <Spacer size='extraLarge' />
              {showHelp ? (
                <Alert severity='info'>
                  Generating a list of pairing codes will give you a one time file to download that may be imported into
                  the factory server. These pairing codes will be guaranteed to be unique and will be reserved for your
                  use.
                </Alert>
              ) : null}
              {generated ? (
                <>
                  <Spacer size='extraLarge' />
                  <Typography>Output</Typography>
                  <Spacer size='medium' />
                  <TextField
                    fullWidth
                    disabled
                    maxRows={100}
                    value={generated}
                    variant='outlined'
                    minRows={10}
                    multiline
                  />
                </>
              ) : null}
            </>
          )}
        </Box>
      </Card>
      <Spacer size='extraLarge' />
      <Card>
        <Box className={classes.box}>
          <Typography variant='h5'>Batch Update Device Model</Typography>
          <Spacer size='medium' />
          <FlexHorizontal>
            <Typography variant='h5'>Model &nbsp;</Typography>
            <Select
              value={batchModel}
              label='Model'
              onChange={e => setBatchModel(e.target.value as string)}>
              {(deviceModelsData?.deviceModels || [])
                .filter(m => !legacyDeviceModels.includes(m.id))
                .map(m => (
                  // @ts-ignore
                  <MenuItem value={m.id}>{m.title}</MenuItem>
                ))}
            </Select>
          </FlexHorizontal>
          <Spacer size='medium' />
          {batchModel ? (
            <>
              <CsvImportButton
                mappings={['deviceId']}
                mainButtonText='Upload Device CSV'
                buttonText='Update Devices'
                types={{ deviceId: 'String' }}
                handleImport={async records => {
                  let succeeded = 0
                  let failed = 0
                  for (const record of records) {
                    try {
                      await apollo.mutate<UpdateDeviceMutation, UpdateDeviceMutationVariables>({
                        mutation: UPDATE_DEVICE_MUTATION,
                        variables: { input: { id: record.deviceId, deviceModel: batchModel } }
                      })
                      succeeded++
                    } catch {
                      failed++
                    }
                  }
                  const total = records.length
                  if (failed === 0) return `Updated ${succeeded} of ${total} devices`
                  return `Updated ${succeeded} of ${total} devices (${failed} failed)`
                }}
              />
              <Spacer size='small' />
              <Typography variant='caption' color='textSecondary'>
                CSV must have a single column header: <strong>deviceId</strong>
              </Typography>
            </>
          ) : (
            <Alert severity='info'>Select a model above before uploading a CSV</Alert>
          )}
        </Box>
      </Card>
    </>
  )
}

export const ReservePairingCodes = () => (
  <ApolloConsumer>{apollo => <ReservePairingCodesFn apollo={apollo} />}</ApolloConsumer>
)
